import './App.css';
import React, { useCallback, useEffect, useState } from 'react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { Analytics } from '@vercel/analytics/react';

import Hero from './components/Hero';
import Meishi from './components/Meishi';
import Section from './components/Section';
import Record from './components/Record';
import LightTable from './components/LightTable';
import { translations } from './translations';
import { socialLinks } from './constants/socialLinks';

function App() {
  const [language, setLanguage] = useState('en');
  const [viewer, setViewer] = useState(null); // { photos, index, caption }
  // The strongest three lead; the rest stay folded until asked for.
  const [allProjects, setAllProjects] = useState(false);
  const FOLDED_PROJECTS = 3;
  const [projectsRef] = useAutoAnimate({ duration: 240, easing: 'cubic-bezier(.2,.7,.3,1)' });

  useEffect(() => {
    setLanguage(localStorage.getItem('language') || 'en');
  }, []);

  useEffect(() => {
    document.title =
      language === 'ja' ? '廣津 和哉 — Kazuya Hirotsu' : 'Kazuya Hirotsu — 廣津 和哉';
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = () => {
    const next = language === 'en' ? 'ja' : 'en';
    setLanguage(next);
    localStorage.setItem('language', next);
  };

  const t = translations[language];
  // Headings are set in both languages whichever way round you read the site,
  // so `other` is always the one you are not reading in.
  const other = translations[language === 'en' ? 'ja' : 'en'];
  const altJp = language === 'en';

  const openPhotos = useCallback((photos, index, caption) => {
    setViewer({ photos, index, caption });
  }, []);

  const navigateViewer = useCallback((step) => {
    setViewer((v) => {
      if (!v) return v;
      const next = (v.index + step + v.photos.length) % v.photos.length;
      return { ...v, index: next };
    });
  }, []);

  const shared = {
    enlargeLabel: t.ui.enlarge,
    onOpenPhotos: openPhotos,
  };

  const work = [
    { key: 'work1', gallery: 'work_amazon' },
    { key: 'work2', gallery: 'work_eniaq' },
    { key: 'work3', gallery: 'work_osaro_sf', cover: true },
    { key: 'work4', gallery: 'work_osaro_tokyo' },
  ];

  const study = [
    { key: 'edu1', gallery: 'edu_master' },
    { key: 'edu2', gallery: 'edu_trento', cover: true },
    { key: 'edu3', gallery: 'edu_bachelor' },
  ];

  const projects = [
    { key: 'project1', gallery: 'proj_cyclecount', cover: true },
    { key: 'project2', gallery: 'proj_naiso' },
    { key: 'project3', gallery: 'proj_madoreader' },
    { key: 'project4', gallery: 'proj_serving' },
    { key: 'project5', gallery: 'proj_boat' },
    { key: 'project7', gallery: 'proj_ardurover' },
    { key: 'project6', gallery: 'proj_fpv' },
  ];

  return (
    <div className="page">
      <a className="skip" href="#now">{t.ui.skip}</a>

      <button className="lang" onClick={toggleLanguage} aria-label={t.ui.langLabel}>
        {t.ui.langLabel}
      </button>

      <main className="sheet">
        <Hero t={t} />

        <Section
          id="now"
          sub="2026 —"
          title={t.ui.titles.now}
          alt={other.ui.titles.now}
          altJp={altJp}
          gallery="about"
          photoAlt={t.ui.photos.about.alt}
          photoCaption={t.ui.photos.about.caption}
          onOpenPhotos={openPhotos}
        >
          <p>{t.about.intro}</p>
        </Section>

        <Section
          id="work"
          sub="2021 — 2026"
          title={t.ui.titles.work}
          alt={other.ui.titles.work}
          altJp={altJp}
        >
          {work.map(({ key, gallery, cover }) => {
            const r = t.experience[key];
            return (
              <Record
                key={key}
                when={r.date}
                title={r.company}
                role={r.position}
                say={r.detail}
                story={r.story}
                link={r.link}
                photoCaption={r.photoCaption}
                gallery={gallery}
                cover={cover}
                {...shared}
              />
            );
          })}
        </Section>

        <Section
          id="study"
          sub="2019 — 2026"
          title={t.ui.titles.study}
          alt={other.ui.titles.study}
          altJp={altJp}
        >
          {study.map(({ key, gallery, cover }) => {
            const r = t.experience[key];
            return (
              <Record
                key={key}
                when={r.date}
                title={r.school}
                role={r.degree}
                say={r.detail}
                story={r.story}
                photoCaption={r.photoCaption}
                gallery={gallery}
                cover={cover}
                {...shared}
              />
            );
          })}
        </Section>

        <Section
          id="made"
          sub="2022 — 2025"
          title={t.ui.titles.made}
          alt={other.ui.titles.made}
          altJp={altJp}
        >
          <div ref={projectsRef}>
            {(allProjects ? projects : projects.slice(0, FOLDED_PROJECTS)).map(({ key, gallery, cover }) => {
              const p = t.projects[key];
              // No role line here on purpose. A record's role is a standing
              // fact about you — a job title, a degree. A project has none,
              // and what used to sit here was a list of tools, which said
              // less than the sentence underneath it.
              return (
                <Record
                  key={key}
                  when={p.date}
                  title={p.title}
                  say={p.description}
                  story={p.story}
                  link={p.link ? { url: p.link, label: t.projects.viewWebsite } : null}
                  photoCaption={p.photoCaption}
                  gallery={gallery}
                  cover={cover}
                  {...shared}
                />
              );
            })}
          </div>

          <div className="more-row">
            <button
              type="button"
              className="show-more"
              aria-expanded={allProjects}
              onClick={() => setAllProjects((v) => !v)}
            >
              {allProjects ? t.projects.showLess : t.projects.showMore}
            </button>

            {allProjects && (
              <a
                className="show-more"
                href="https://github.com/kazuyahirotsu"
                target="_blank"
                rel="noreferrer"
              >
                {t.projects.viewMore}
              </a>
            )}
          </div>
        </Section>

        <Section
          id="papers"
          sub="2023 · 2024"
          title={t.ui.titles.papers}
          alt={other.ui.titles.papers}
          altJp={altJp}
          gallery="research"
          photoAlt={t.ui.photos.research.alt}
          photoTop="200px"
          onOpenPhotos={openPhotos}
        >
          {[t.publications.pub1, t.publications.pub2].map((pub) => (
            <div className="pub" key={pub.url}>
              <p className="cite">{pub.date}</p>
              <h3>{pub.title}</h3>
              <p>{pub.authors}</p>
              <p className="cite">{pub.cite}</p>
              <p style={{ marginTop: '10px' }}>
                <a className="linkout" href={pub.url} target="_blank" rel="noreferrer">
                  {t.publications.viewPaper}
                </a>
              </p>
            </div>
          ))}
        </Section>

        <Section
          id="grants"
          sub="2022 — 2025"
          title={t.ui.titles.grants}
          alt={other.ui.titles.grants}
          altJp={altJp}
        >
          {[t.grants.grant1, t.grants.grant2, t.grants.grant3].map((g) => (
            <div className="pub" key={g.name + g.date}>
              <p className="cite">{g.date}</p>
              <h3>{g.name}</h3>
              <p>{g.org}</p>
              <p className="amount">{g.amount}</p>
            </div>
          ))}
        </Section>

        <Section
          id="margins"
          title={t.ui.titles.margins}
          alt={other.ui.titles.margins}
          altJp={altJp}
          gallery="outside"
          photoAlt={t.ui.photos.outside.alt}
          photoCaption={t.ui.photos.outside.caption}
          onOpenPhotos={openPhotos}
        >
          <p>{t.about.margins}</p>
        </Section>

        <Section
          id="hello"
          sub="Tokyo"
          title={t.ui.titles.hello}
          alt={other.ui.titles.hello}
          altJp={altJp}
        >
          <p>{t.contact.subtitle}</p>
          <Meishi
            email="hrtkzy4444@outlook.jp"
            socials={socialLinks.filter((l) => l.label !== 'Email')}
            language={language}
          />
        </Section>

        <footer className="colophon" role="contentinfo">
          <span>© 2026 Kazuya Hirotsu</span>
          <span>{t.footer.location}</span>
        </footer>
      </main>

      {viewer && (
        <LightTable
          photos={viewer.photos}
          index={viewer.index}
          caption={viewer.caption}
          onNavigate={navigateViewer}
          onClose={() => setViewer(null)}
        />
      )}

      <Analytics />
    </div>
  );
}

export default App;
